import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { SkeletonFiller } from 'components/SkeletonFiller';
import { AccountGate } from 'components/account/AccountGate';
import { CampaignPicker } from 'components/campaign/CampaignPicker';
import { NoCampaigns } from 'components/campaign/NoCampaigns';
import { FormCombobox } from 'components/form/FormCombobox';
import { FormSelect } from 'components/form/FormSelect';
import { WidgetForm } from 'components/widgets/WidgetForm';
import { WIDGET_TYPE_LABELS, defaultsFromSchema, sanitizeConfig } from 'lib/element-config';
import { WidgetType, createApi } from 'lib/gb-api';
import { getWidgetAccessToken, getWidgetProp, setWidgetProp } from 'lib/wix-widget-sdk';

const SETUP_TYPES: { value: WidgetType; label: string }[] = [
  { value: 'button', label: 'Donation button' },
  { value: 'giving-form', label: 'Donation form' },
  { value: 'signup-form', label: 'Signup form' },
];

type SetupMode = 'create_new' | 'use_existing';

interface SetupValues {
  mode: SetupMode;
  type: WidgetType;
  campaign_id: number | '';
  existing_display_id: string;
}

export const SettingsPanel = () => {
  const api = useMemo(() => createApi(getWidgetAccessToken), []);
  const queryClient = useQueryClient();
  const [displayId, setDisplayId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [canvasSyncError, setCanvasSyncError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getWidgetProp('id')
      .then(currentId => {
        if (cancelled) return;
        if (currentId && currentId.trim()) {
          setDisplayId(currentId.trim());
        }
      })
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const instanceQuery = useQuery({
    queryKey: ['wix', 'instance'],
    queryFn: api.decodeInstance,
    refetchOnWindowFocus: true,
  });

  const isLinked = instanceQuery.data?.is_linked === true;
  const accountAid = instanceQuery.data?.account_aid ?? '';
  const accountId = instanceQuery.data?.account_id ?? null;

  const campaignsQuery = useQuery({
    queryKey: ['wix', 'campaigns'],
    queryFn: api.listCampaigns,
    enabled: isLinked && !displayId,
  });

  const optionsQuery = useQuery({
    queryKey: ['wix', 'element-options'],
    queryFn: api.getElementOptions,
    enabled: isLinked && !displayId,
  });

  const elementsQuery = useQuery({
    queryKey: ['wix', 'elements'],
    queryFn: api.listElements,
    enabled: isLinked && !displayId,
  });

  const setupForm = useForm<SetupValues>({
    defaultValues: { type: 'button', campaign_id: '', existing_display_id: '', mode: 'create_new' },
  });

  const createMutation = useMutation({
    mutationFn: api.createElement,
    onSuccess: element => {
      queryClient.invalidateQueries({ queryKey: ['wix', 'elements'] });
      setWidgetProp('id', element.display_id).catch(e => {
        setCanvasSyncError(e instanceof Error ? e.message : 'Could not sync widget to canvas.');
      });
      setDisplayId(element.display_id);
    },
  });

  useEffect(() => {
    setWidgetProp('platform', 'wix').catch(e => {
      setCanvasSyncError(e instanceof Error ? e.message : 'Could not sync widget to canvas.');
    });
  }, []);

  useEffect(() => {
    if (accountAid) {
      setWidgetProp('account', accountAid).catch(e => {
        setCanvasSyncError(e instanceof Error ? e.message : 'Could not sync widget to canvas.');
      });
    }
  }, [accountAid]);

  const [setupType, setupCampaignId, setupMode, selectedExistingId] = setupForm.watch([
    'type',
    'campaign_id',
    'mode',
    'existing_display_id',
  ]);
  const setupRequiresCampaign = setupType !== 'signup-form';
  const noCampaigns = campaignsQuery.isSuccess && (campaignsQuery.data ?? []).length === 0;

  const shouldShowAddWidgetButton =
    setupMode === 'use_existing' ||
    (setupMode === 'create_new' && !setupRequiresCampaign) ||
    (setupRequiresCampaign && !noCampaigns);

  useEffect(() => {
    const campaigns = campaignsQuery.data ?? [];
    if (campaigns.length > 0 && !setupCampaignId) {
      setupForm.setValue('campaign_id', campaigns[0].id);
    }
  }, [campaignsQuery.data, setupCampaignId, setupForm]);

  const existingWidgets = useMemo(
    () =>
      (elementsQuery.data ?? []).map(el => ({
        value: el.display_id,
        label: [el.name || el.display_id, WIDGET_TYPE_LABELS[el.type], el.campaign?.title]
          .filter(Boolean)
          .join(' · '),
        searchTerms: [el.display_id],
      })),
    [elementsQuery.data],
  );
  const hasExistingElements = existingWidgets.length > 0;

  if (instanceQuery.isLoading || !hydrated) {
    return <SkeletonFiller />;
  }

  if (instanceQuery.error || !instanceQuery.data) {
    return (
      <div className="p-4 text-sm text-red-600">Couldn't verify Wix install. Try reloading.</div>
    );
  }

  if (!isLinked) {
    return (
      <div className="p-4">
        <AccountGate api={api} instance={instanceQuery.data} />
      </div>
    );
  }

  if (displayId) {
    return (
      <>
        {canvasSyncError && (
          <div className="m-4 mb-0 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {canvasSyncError}
          </div>
        )}
        <WidgetForm
          api={api}
          accountAid={accountAid}
          accountId={accountId}
          editingDisplayId={displayId}
          compact
          autosave
          onCleared={async () => {
            try {
              await setWidgetProp('id', '');
              setDisplayId(null);
              setCanvasSyncError(null);
            } catch (e) {
              setCanvasSyncError(
                e instanceof Error ? e.message : 'Could not clear the widget from the canvas.',
              );
            }
          }}
          onSaved={async () => {
            queryClient.invalidateQueries({ queryKey: ['wix', 'elements'] });
            try {
              await setWidgetProp('refresh-token', String(Date.now()));
              setCanvasSyncError(null);
            } catch (e) {
              setCanvasSyncError(
                e instanceof Error ? e.message : 'Could not refresh canvas preview.',
              );
            }
          }}
        />
      </>
    );
  }

  return (
    <form
      className="flex flex-col gap-6 p-4"
      onSubmit={setupForm.handleSubmit(values => {
        if (values.mode === 'use_existing') {
          if (!values.existing_display_id) {
            return;
          }
          setWidgetProp('id', values.existing_display_id).catch(e => {
            setCanvasSyncError(e instanceof Error ? e.message : 'Could not sync widget to canvas.');
          });
          setDisplayId(values.existing_display_id);
          return;
        }

        const schema = optionsQuery.data?.forms[values.type];
        if (!schema) {
          return;
        }
        createMutation.mutate({
          type: values.type,
          campaign_id: values.campaign_id === '' ? undefined : Number(values.campaign_id),
          config: sanitizeConfig(values.type, defaultsFromSchema(values.type, schema)),
        });
      })}
    >
      {setupMode === 'use_existing' ? (
        <>
          <Button
            variant="outline"
            color="neutral"
            size="sm"
            onClick={() => setupForm.setValue('mode', 'create_new')}
          >
            Create a new widget
          </Button>
          <div className="flex items-center gap-2">
            <span className="w-full border-b border-gray-300" />
            <p className="text-center font-medium text-gray-500 uppercase">or</p>
            <span className="w-full border-b border-gray-300" />
          </div>
          <FormCombobox
            name="existing_display_id"
            control={setupForm.control}
            label="Select an existing widget"
            placeholder="Select a widget…"
            searchPlaceholder="Search widgets"
            options={existingWidgets}
            resource="widget"
          />
        </>
      ) : (
        <>
          {hasExistingElements && (
            <>
              <Button
                variant="outline"
                color="neutral"
                size="sm"
                onClick={() => setupForm.setValue('mode', 'use_existing')}
              >
                Use an existing widget
              </Button>
              <div className="flex items-center gap-2">
                <span className="w-full border-b border-gray-300" />
                <p className="text-center font-medium text-gray-500 uppercase">or</p>
                <span className="w-full border-b border-gray-300" />
              </div>
            </>
          )}

          <FormSelect
            name="type"
            control={setupForm.control}
            label="New widget type"
            options={SETUP_TYPES}
          />

          {setupRequiresCampaign && (
            <>
              <div className="-mx-4 border-b" />

              {campaignsQuery.isLoading ? (
                <div className="-ml-6 flex items-center justify-center gap-4 p-4 text-sm text-gray-500">
                  <Loader variant="static" />
                  <p>Loading campaigns…</p>
                </div>
              ) : noCampaigns ? (
                <NoCampaigns accountId={accountId} compact />
              ) : (
                <>
                  <div className="-mx-4 flex flex-col gap-2">
                    <div className="flex flex-col gap-1.5 px-4">
                      <p className="text-center text-lg leading-6 font-medium">
                        Where should donations from this Wix widget go?
                      </p>
                      <p className="text-center text-xs text-gray-600">
                        All donations collected from this widget will be sent to the Givebutter
                        campaign you select below. You can update this at any time.
                      </p>
                    </div>
                    <CampaignPicker
                      name="campaign_id"
                      control={setupForm.control}
                      label="Campaign"
                      campaigns={campaignsQuery.data ?? []}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {shouldShowAddWidgetButton && (
        <Button
          type="submit"
          variant="solid"
          color="primary"
          disabled={
            setupMode === 'use_existing'
              ? !selectedExistingId || selectedExistingId.length === 0
              : createMutation.isPending ||
                !optionsQuery.isSuccess ||
                (setupRequiresCampaign && (noCampaigns || !setupCampaignId))
          }
        >
          {createMutation.isPending ? 'Adding…' : 'Add widget'}
        </Button>
      )}
    </form>
  );
};
